import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUser } from '../../firebase/firestore';
import Logo from '../Logo';
import PersonalDetailsForm, { PersonalDetails } from './PersonalDetailsForm';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<'phone' | 'otp' | 'personal-details'>('phone');
  const [userId, setUserId] = useState<string>('');
  
  const { sendOTP, verifyOTP } = useAuth();
  const navigate = useNavigate();

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    
    const limitedDigits = digits.slice(0, 10);
    
    return limitedDigits;
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (phoneNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = `+91${phoneNumber}`;
      const confirmation = await sendOTP(formattedPhone);
      setConfirmationResult(confirmation);
      setStep('otp');
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const user = await verifyOTP(confirmationResult, otp);
      
      if (user && typeof user === 'object' && 'uid' in user) {
        setUserId(user.uid);
        
        toast.success('Login successful!');
        navigate('/');
      } else {
        toast.success('Login successful!');
        navigate('/');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const formattedPhone = `+91${phoneNumber}`;
      const confirmation = await sendOTP(formattedPhone);
      setConfirmationResult(confirmation);
      toast.success('OTP resent successfully!');
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setOtp('');
    setConfirmationResult(null);
  };

  const handlePersonalDetailsComplete = async (details: PersonalDetails) => {
    if (!userId) {
      toast.error('User ID not found. Please try logging in again.');
      return;
    }

    setLoading(true);
    try {
      await updateUser(userId, {
        name: details.name,
        email: details.email || '',
        city: details.city,
        pincode: details.pincode
      });

      toast.success('Profile completed successfully! Welcome to Shetkari Agro Fresh!');
      navigate('/');
    } catch (error) {
      console.error('Error saving personal details:', error);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'personal-details') {
    return (
      <PersonalDetailsForm
        phoneNumber={`+91${phoneNumber}`}
        onComplete={handlePersonalDetailsComplete}
        loading={loading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-primary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {}
        <div className="text-center mb-8">
          {}
          <div className="flex justify-center">
            <Logo size="xl" showText={false} />
          </div>
          
          {}
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Shetkari Agro Fresh</h1>
          <p className="text-sm text-gray-600 mb-4">Farm Fresh • Home Delivered</p>
          
          {}
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {step === 'phone' ? 'Welcome!' : 'Verify Your Number'}
          </h2>
          <p className="text-gray-600">Fresh farm products delivered to your doorstep</p>
        </div>

        {}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6">
          {step === 'phone' ? (
            <form onSubmit={handlePhoneSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="absolute inset-y-0 left-10 flex items-center pointer-events-none">
                    <span className="text-gray-500">+91</span>
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                    placeholder="Enter your phone number"
                    className="input-field pl-20"
                    required
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading || phoneNumber.length !== 10}
                className="btn-primary w-full flex items-center justify-center"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  We've sent a 6-digit code to +91{phoneNumber}
                </p>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  className="input-field text-center text-lg tracking-widest"
                  maxLength={6}
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="btn-primary w-full mb-4"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Verify OTP'
                )}
              </button>
              
              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={handleBackToPhone}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Change Number
                </button>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-primary-500 hover:text-primary-600"
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}
        </div>

        {}
        <p className="text-center text-xs text-gray-500 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Login;
